import React from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getVgsapBulletinDetail, getVgsapBulletinViewData } from "../../service/SalesNewsVgsap";
import { PageLoader } from "../../component/loader/PLoader";
import ErrorHandler from "../../component/errors/ErrorHandler";
import { Box, Typography, Paper, Breadcrumbs, Chip, Divider } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import "./salesNewsVgsap.css";
import { formatDateOrNA as formatDate, DetailField } from "./vgsapUtils";

function BulletinDetail() {
	const { bulletinNo } = useParams();
	const { t } = useTranslation();
	const location = useLocation();
	// Prefer router state (in-app nav); fall back to the query param so the breadcrumb
	// still shows the "Promotion {id}" trail after a page refresh (state is lost on reload).
	const fromPromoQuery = new URLSearchParams(location.search).get("fromPromo");
	const fromPromo = location.state?.fromPromo || fromPromoQuery || null;

	// Main bulletin detail
	const { data: bulletin, isLoading, error } = useQuery({
		queryKey: ["vgsapBulletinDetail", bulletinNo],
		queryFn: () => getVgsapBulletinDetail(bulletinNo),
	});

	// Sub-data queries (queryType 2=designations, 3=companies, 4=cpg, 5=models, 6=customers)
	const { data: designations } = useQuery({
		queryKey: ["vgsapBulletinViewData", bulletinNo, 2],
		queryFn: () => getVgsapBulletinViewData(bulletinNo, 2),
		enabled: !!bulletinNo,
	});

	const { data: companies } = useQuery({
		queryKey: ["vgsapBulletinViewData", bulletinNo, 3],
		queryFn: () => getVgsapBulletinViewData(bulletinNo, 3),
		enabled: !!bulletinNo,
	});

	const { data: cpgData } = useQuery({
		queryKey: ["vgsapBulletinViewData", bulletinNo, 4],
		queryFn: () => getVgsapBulletinViewData(bulletinNo, 4),
		enabled: !!bulletinNo,
	});

	const { data: modelsData } = useQuery({
		queryKey: ["vgsapBulletinViewData", bulletinNo, 5],
		queryFn: () => getVgsapBulletinViewData(bulletinNo, 5),
		enabled: !!bulletinNo,
	});

	const { data: customers } = useQuery({
		queryKey: ["vgsapBulletinViewData", bulletinNo, 6],
		queryFn: () => getVgsapBulletinViewData(bulletinNo, 6),
		enabled: !!bulletinNo,
	});

	if (isLoading) return <PageLoader show={true} />;
	if (error) return <ErrorHandler title={t("Something went wrong")} desc={t("Unable to load bulletin details")} />;
	if (!bulletin) return <ErrorHandler title={t("Not Found")} desc={t("Bulletin not found")} />;

	// Compute associated models count
	const modelsCount = Array.isArray(modelsData) ? modelsData.length : 0;

	// Filter designations/companies/cpg associated with this bulletin
	const associatedDesignations = (designations || []).filter(d => d.bulletin_no === bulletinNo);
	const associatedCompanies = (companies || []).filter(c => c.bulletin_no === bulletinNo);
	const associatedCPG = (cpgData || []).filter(c => c.bulletin_no === bulletinNo);

	// Map region
	const regionMap = { 'US00': 'US', 'CA00': 'CANADA', 'BOTH': 'BOTH' };
	const region = regionMap[bulletin.company_id] || bulletin.company_id || 'N/A';

	return (
		<Box sx={{ p: 3 }} className="vgsap__page">
			{/* Breadcrumb */}
			<Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
				<Link to="/sales-news/sales-news-vgsap" style={{ textDecoration: "none", color: "#1976d2" }}>
					{t("Sales News (Vistex GSAP)")}
				</Link>
				{fromPromo && (
					<Link to={`/sales-news/sales-news-vgsap/promotion/${fromPromo}`} style={{ textDecoration: "none", color: "#1976d2" }}>
						{t("Promotion")} {fromPromo}
					</Link>
				)}
				<Typography color="text.primary">{bulletinNo}</Typography>
			</Breadcrumbs>

			{/* Bulletin Detail Card */}
			<Paper sx={{ p: 3 }}>
				<Typography variant="h5" gutterBottom>
					{t("Sales News")} #{bulletinNo}
				</Typography>

				{/* Detail fields — full RefLib layout */}
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5, mt: 2 }}>
					<DetailField label={t("Sales News ID")} value={bulletin.bulletin_no} />
					<DetailField label={t("Sales News Date")} value={formatDate(bulletin.bulletin_dt)} />
					<DetailField label={t("Region")} value={region} />
					<DetailField label={t("Effective Date")} value={formatDate(bulletin.effective_dt)} />
					<DetailField label={t("Promo Start")} value={formatDate(bulletin.promo_start)} />
					<DetailField label={t("Promo End")} value={formatDate(bulletin.promo_end)} />
					<DetailField label={t("Created By")} value={bulletin.create_opid ? `${bulletin.create_opid} on ${formatDate(bulletin.create_dt)}` : 'N/A'} />
					<DetailField label={t("Last Modified By")} value={bulletin.maint_opid ? `${bulletin.maint_opid} on ${formatDate(bulletin.maint_dt)}` : 'N/A'} />
					<DetailField label={t("Requested By")} value={bulletin.request} />
				</Box>

				{/* Description — full width */}
				<Box sx={{ mt: 2.5 }}>
					<DetailField label={t("Description")} value={bulletin.bulletin_desc} />
				</Box>

				<Divider sx={{ my: 3 }} />

				{/* Type, Business Unit, Rebate Portal */}
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
					<DetailField label={t("Type")} value={bulletin.bulletin_type} />
					<DetailField label={t("Business Unit")} value={bulletin.prodgroup} />
					<DetailField label={t("Rebate Portal")} value={bulletin.rebate_portal && bulletin.rebate_portal !== 'NULL' ? 'Yes' : 'No'} />
				</Box>

				<Divider sx={{ my: 3 }} />

				{/* Associated File (PDF) */}
				<Box sx={{ mb: 2 }}>
					<Typography variant="subtitle2" color="text.secondary">{t("Associated File")}</Typography>
					<a href={bulletin.pdfLink} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
						{bulletinNo}.pdf
					</a>
				</Box>

				<Divider sx={{ my: 3 }} />

				{/* Designation(s) */}
				<Box sx={{ mb: 2 }}>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>{t("Designation(s)")}</Typography>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
						{associatedDesignations.length > 0
							? associatedDesignations.map((d, i) => (
								<Chip key={i} label={d.designation} size="small" variant="outlined" />
							))
							: <Typography variant="body2" color="text.secondary">{bulletin.designation || 'N/A'}</Typography>
						}
					</Box>
				</Box>

				{/* Company(s) */}
				<Box sx={{ mb: 2 }}>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>{t("Company(s)")}</Typography>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
						{associatedCompanies.length > 0
							? associatedCompanies.map((c, i) => (
								<Chip key={i} label={c.company} size="small" variant="outlined" />
							))
							: <Typography variant="body2" color="text.secondary">N/A</Typography>
						}
					</Box>
				</Box>

				{/* CPG(s) */}
				<Box sx={{ mb: 2 }}>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>{t("CPG(s)")}</Typography>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
						{associatedCPG.length > 0
							? associatedCPG.map((c, i) => (
								<Chip key={i} label={`${c.scd_code} - ${c.schedule_disp || c.schedule}`} size="small" variant="outlined" />
							))
							: <Typography variant="body2" color="text.secondary">{bulletin.scd_code || 'N/A'}</Typography>
						}
					</Box>
				</Box>

				{/* Customers Sold To */}
				<Box sx={{ mb: 2 }}>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>{t("Customers Sold To")}</Typography>
					{customers && customers.length > 0
						? customers.map((c, i) => (
							<Typography key={i} variant="body2">
								{c.customersoldto_number} — {c.customerssoldto_name}
							</Typography>
						))
						: <Typography variant="body2" color="text.secondary">N/A</Typography>
					}
				</Box>

				<Divider sx={{ my: 3 }} />

				{/* Associated Models */}
				<Box sx={{ mb: 2 }}>
					<Typography variant="subtitle2" color="text.secondary">{t("Associated Models")}</Typography>
					{modelsCount > 0 ? (
						<Link
							to={`/sales-news/sales-news-vgsap/detail/${bulletinNo}/models`}
							style={{ color: "#1976d2", textDecoration: "none" }}
						>
							{modelsCount} {t("Model(s)")}
						</Link>
					) : (
						<Typography variant="body1">0 {t("Model(s)")}</Typography>
					)}
				</Box>
			</Paper>

			<Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
				NOTE: Supporting Files must be uploaded through CORE prior to the Email blast (currently 1st and 15th of the month)
			</Typography>
		</Box>
	);
}

export default BulletinDetail;
